import type { TFunction } from "i18next";
import { z } from "zod";

// DFT-0081: mirrors bay-hire-service's BayHireRequestService.MAX_TOTAL_AMOUNT, itself derived
// from total_amount's NUMERIC(12,2) column capacity (10 integer digits, 2 decimal places). No
// shared-constants mechanism exists between the two repos - kept in sync by comment reference.
const MAX_TOTAL_AMOUNT = 9999999999.99;

export const PRE_APPROVE_DIALOG_FIELDS = {
  DATE_FROM_APPROVED: "dateFromApproved",
  DATE_TO_APPROVED: "dateToApproved",
  TOTAL_AMOUNT: "totalAmount",
  PRE_APPROVAL_REMARKS: "preApprovalRemarks",
} as const;

/**
 * DFT-0044 bug fix (2026-08-03): the amount rule IS mirrored here after all - see the retraction
 * note at the foot of this file. `isChargeable` is derived by the caller from the request's
 * `paymentType` (EXEMPTED is not chargeable), the same derivation the domain performs.
 */
export const makePreApproveDialogSchema = (isChargeable: boolean, t: TFunction) =>
  z
    .object({
      dateFromApproved: z.string().min(1, "Date from is required"),
      dateToApproved: z.string().min(1, "Date to is required"),
      totalAmount: z.string().optional(),
      // DFT-0078: capped at 1000 to match `remarks`'s own bound on the request itself
      // (`bay-hire-service/api/bay-hire-api.yaml`, `maxLength: 1000`), for consistency across the
      // two remarks-shaped fields in this flow.
      //
      // 🔴 Unlike `remarks`, this is a FRONTEND-ONLY convention, not a discovered backend
      // contract: `preApprovalRemarks` has no `maxLength` anywhere in either OpenAPI spec
      // (`bff-bay-hire-service/api/bay-hire-bff-api.yaml`'s two occurrences are unbounded
      // `nullable: true` strings, and the domain spec does not define this field at all -
      // pre-approval remarks is a BFF-only concept). Chosen as a sensible default rather than
      // discovered; a separate task should add the matching `maxLength: 1000` to the BFF spec so
      // the two sides actually agree, rather than merely both happening to say 1000.
      preApprovalRemarks: z
        .string()
        .max(1000, "Remarks must be 1000 characters or fewer.")
        .optional(),
      // DFT-0044 slice C: bayAssignments is GONE from this form. Operations no longer selects bays -
      // the Shipping Agent chooses them at request time and they are immutable thereafter (D35/D43),
      // so the pre-approve contract has no such field. The old AC15 client-side mirror ("at least one
      // bay is required") is removed with it: mirroring a rule that no longer exists would have made
      // the dialog refuse to submit for a reason the server does not have.
    })
    .superRefine((data, ctx) => {
      if (
        data.dateFromApproved &&
        data.dateToApproved &&
        data.dateToApproved < data.dateFromApproved
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dateToApproved"],
          message: "Date to must be on or after date from",
        });
      }

      // 🔴 DFT-0044 bug fix (2026-08-03) - RETRACTS the note that used to sit here.
      //
      // The previous comment argued this rule should NOT be mirrored, because "the domain answers
      // 400" and threading the payment type in would only "reproduce a check the server already
      // makes correctly". The server does make it correctly; the problem is what happens next.
      //
      // Observed: Ops pre-approved a MONTHLY request with the amount left blank.
      // `usePreApproveDialog` coerced the empty field to 0, the domain answered 400 ("total amount
      // must be positive when chargeable"), `BayHireDomainProxy` classified the 4xx as a
      // NON-RETRYABLE ApplicationFailure, and the Temporal workflow FAILED PERMANENTLY. The request
      // was left at REQUESTED with no UI feedback and no way forward - a dead request created by a
      // blank input box.
      //
      // So a 400 here is not a recoverable server validation error the way it is on a synchronous
      // endpoint: it is terminal. Client-side prevention is therefore load-bearing, not duplication.
      if (isChargeable) {
        const amount = Number(data.totalAmount);
        if (!data.totalAmount?.trim() || Number.isNaN(amount) || amount <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["totalAmount"],
            message: "Enter a total amount greater than 0",
          });
        }
      }

      // DFT-0081: unconditional - unlike the check above, NOT scoped to isChargeable. This
      // field is rendered (and can be typed into) for EXEMPTED requests too, and the DB
      // column's capacity is fixed regardless of payment type: an EXEMPTED request with an
      // over-limit amount would crash persistence the same way a chargeable one would (the
      // same terminal-Temporal-failure shape DFT-0044 fixed for a zero amount).
      if (data.totalAmount?.trim()) {
        const amount = Number(data.totalAmount);
        if (!Number.isNaN(amount) && amount > MAX_TOTAL_AMOUNT) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["totalAmount"],
            message: t("bay-hire.pre-approve.amount-exceeds-max", { max: MAX_TOTAL_AMOUNT }),
          });
        }
      }
    });

/**
 * Retained for callers/tests that do not care about the amount rule (EXEMPTED requests) or a
 * real `t`. DFT-0081: the identity function is only ever used for this export's `z.infer` type
 * shape below - it never actually runs (no validation is triggered by declaring the type), so a
 * real i18n instance isn't needed here.
 */
export const preApproveDialogSchema = makePreApproveDialogSchema(
  false,
  ((key: string) => key) as TFunction,
);

export type PreApproveDialogFormData = z.infer<typeof preApproveDialogSchema>;

export const preApproveDialogDefaultValues: PreApproveDialogFormData = {
  dateFromApproved: "",
  dateToApproved: "",
  totalAmount: "",
  preApprovalRemarks: "",
};
